import React, { useState, useEffect, useCallback } from 'react';
import { Flag, X, AlertCircle, FileX, FileWarning, BookOpen, Clock, MessageSquare, Link, Plus } from 'lucide-react';

const ModalReportarError = ({ isOpen, onClose, onSend, solicitudId }) => {
  const [categoria, setCategoria] = useState('');
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Opciones con Iconos para centrar la experiencia visual
  const OPCIONES = [
    { id: "No hay archivos adjuntos", icon: <FileX size={20} />, label: "Sin archivos" },
    { id: "Archivo corrupto o no abre", icon: <FileWarning size={20} />, label: "Archivo dañado" },
    { id: "Materia/Categoría incorrecta", icon: <BookOpen size={20} />, label: "Materia errónea" },
    { id: "Fecha de entrega imposible", icon: <Clock size={20} />, label: "Fecha imposible" },
    { id: "Descripción incompleta o confusa", icon: <MessageSquare size={20} />, label: "Info. confusa" },
    { id: "Link de acceso roto o requiere clave", icon: <Link size={20} />, label: "Link roto" },
    { id: "Otro", icon: <Plus size={20} />, label: "Otro motivo" },
  ];

  const handleClose = useCallback(() => {
    setCategoria('');
    setComentario('');
    onClose();
  }, [onClose]);

  const handleEnviar = async () => {
    if (!categoria || enviando) return;

    try {
      setEnviando(true);
      // Esperamos a que la función onSend (que viene del padre) termine
      await onSend({ solicitudId, categoria, comentario });
      
      // Si llegó aquí es porque no hubo error, así que cerramos y limpiamos
      handleClose(); 
    } catch (error) {
      console.error("Error al enviar el reporte:", error);
      // Opcional: podrías poner un mensaje de error aquí para el usuario
    } finally {
      setEnviando(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && handleClose();
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={handleClose}>
      <div 
        className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden animate-in fade-in zoom-in duration-250"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con estilo más limpio */}
        <div className="bg-amber-500 p-6 flex justify-between items-center text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Flag size={22} fill="currentColor" />
            </div>
            <div>
              <h3 className="font-bold text-xl">Reportar Problema</h3>
              <p className="text-amber-100 text-xs">Solicitud #{solicitudId}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-black/10 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-8">
          <label className="block text-center text-slate-700 font-bold mb-6 text-lg">
            ¿Qué problema encontraste?
          </label>

          {/* GRID DE CATEGORÍAS CENTRADO */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
            {OPCIONES.map((opcion) => (
              <button
                key={opcion.id}
                onClick={() => setCategoria(opcion.id)}
                className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all duration-200 gap-2 ${
                  categoria === opcion.id 
                  ? 'border-amber-500 bg-amber-50 text-amber-700 shadow-md scale-105' 
                  : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-amber-200 hover:bg-white'
                }`}
              >
                <div className={`${categoria === opcion.id ? 'text-amber-500' : 'text-slate-400'}`}>
                  {opcion.icon}
                </div>
                <span className="text-[11px] font-bold leading-tight text-center">
                  {opcion.label}
                </span>
              </button>
            ))}
          </div>

          {/* COMENTARIO */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-600 ml-1">Detalles adicionales</label>
            <textarea 
              className="w-full p-4 border-2 border-slate-100 rounded-2xl bg-slate-50 h-24 focus:ring-0 focus:border-amber-500 outline-none transition-all resize-none text-slate-700 placeholder:text-slate-400"
              placeholder="Ej: El archivo adjunto pide una contraseña que no está..."
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            ></textarea>
          </div>

          {/* ACCIONES */}
          <div className="flex gap-4 mt-8">
            <button
              onClick={handleClose}
              className="flex-1 px-6 py-3.5 text-slate-500 font-bold rounded-2xl hover:bg-slate-100 transition-colors"
            >
              Cerrar
            </button>
            <button
              disabled={!categoria || enviando}
              onClick={handleEnviar} // <-- Cambiamos por la nueva función interna
              className="flex-[2] px-6 py-3.5 bg-amber-500 text-white rounded-2xl font-bold hover:bg-amber-600 disabled:opacity-50 shadow-lg shadow-amber-200 transition-all flex items-center justify-center gap-2"
            >
              {enviando ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Enviando...
                </>
              ) : (
                'Enviar Reporte'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalReportarError;